import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Trash2 } from "lucide-react";

import { addGiftCardApi, editGiftCardApi } from "@/api/giftCard";
import { getAllCustomerApi, getCustomerByIdApi } from "@/api/customer/customer";
import FormInputField from "@/components/lib/from-input-field";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import CustomerMultiSelect from "@/components/loyalty/CustomerMultiSelect";
import { loyaltyLinks } from "@/components/lib/SidebarLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "react-hot-toast";

import CreateItem from "@/components/dialog/menu-manage/common-dialog/create-item";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import {
  clearItemVariants,
  removeItemVariant,
  setItemVariants,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import { fetchLoyaltyTemplates } from "@/redux/slice/loyalty/templeteSlice";
import useAuth from "@/hooks/useAuth";
import {
  getComponentByTemplate,
  getTemplateForRecord,
  sortTemplatesBySerial,
} from "@/lib/loyalty-template-utils";
import GiftTemplate1 from "@/pages/loyalty/templets/card/gift-card/templete1";
import GiftTemplate2 from "@/pages/loyalty/templets/card/gift-card/templete2";
import GiftTemplate3 from "@/pages/loyalty/templets/card/gift-card/templete3";
import GiftTemplate4 from "@/pages/loyalty/templets/card/gift-card/templete4";
import LoyaltyCreateSkeleton from "@/components/loyalty/LoyaltyCreateSkeleton";

const GIFT_TEMPLATE_COMPONENTS = [
  GiftTemplate1,
  GiftTemplate2,
  GiftTemplate3,
  GiftTemplate4,
];

const ORDER_TYPE_OPTIONS = [
  { label: "Pickup", value: "pickup" },
  { label: "In-house", value: "in_house" },
  { label: "Table", value: "table" },
  { label: "Delivery", value: "delivery" },
];

const PAYMENT_METHOD_OPTIONS = [
  { label: "Cash", value: "cash" },
  { label: "Card", value: "card" },
  { label: "Pay by Link", value: "pay_by_link" },
  { label: "Cash on Delivery", value: "cash_on_delivery" },
];

const toggleArrayValue = (currentValues, value, checked) => {
  if (checked) {
    return currentValues.includes(value) ? currentValues : [...currentValues, value];
  }

  return currentValues.filter((item) => item !== value);
};

function AddGiftCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updateItem = location.state;
  const { type } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [customer, setCustomer] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);

  const { flatMenus, getLoading: menuLoading } = useSelector(
    (state) => state.menuManage.menu,
  );
  const { templates, getLoading: templateLoading } = useSelector(
    (state) => state.loyalty.template,
  );
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);

  const form = useForm({
    resolver: zodResolver(addNewGiftCardSchema),
    mode: "onChange",
    defaultValues: {
      actionType: "add",
      type: "fixed",
      reward: "",
      gift_card_items: [],
      note: "",
      template_id: "",
      expiry_date: "",
      status: "active",
      usage_limit: "",
      is_multiple_usage: false,
      terms_and_conditions: false,
      is_conjunction: false,
      template_note: "",
      template_note_enabled: true,
      order_types: [],
      payment_methods: [],
      payment_method_status: false,
      order_type_status: false,
    },
  });

  const { reset } = form;
  const giftType = form.watch("type");
  const isFoodItem = giftType === "food_item";
  const isPercentage = giftType === "percentage";
  const isFixed = giftType === "fixed";
  const isMultipleUsage = form.watch("is_multiple_usage");
  const giftCardItems = form.watch("gift_card_items");

  useEffect(() => {
    if (
      giftType === "food_item" &&
      user?.restaurant_id &&
      flatMenus.length === 0
    ) {
      dispatch(fetchAllFlatMenu(user.restaurant_id));
    }
  }, [dispatch, flatMenus.length, giftType, user?.restaurant_id]);

  useEffect(() => {
    getAllCustomerApi()
      .then((res) => {
        if (res.status === "success") {
          setCustomer(res.data.customers);

          const orderCustomer = location.state?.customer;
          if (orderCustomer?.customer_id) {
            const matchedCustomer = res.data.customers.find(
              (item) => item?.id == orderCustomer.customer_id,
            );
            if (matchedCustomer) {
              setSelectedUser([matchedCustomer]);
            } else {
              // Fallback: customer not in paginated list, fetch by id directly
              getCustomerByIdApi(orderCustomer.customer_id)
                .then((custRes) => {
                  if (custRes?.status === "success" && custRes?.data) {
                    const customerObj =
                      custRes.data.customer || custRes.data;
                    if (customerObj && customerObj.id) {
                      setSelectedUser([customerObj]);
                    }
                  }
                })
                .catch(() => { });
            }
          } else if (updateItem) {
            const previousCustomerId =
              updateItem?.gift_card_histories?.[0]?.customer_id;
            if (previousCustomerId) {
              const previousSelectedUser = res.data.customers.find(
                (item) => item?.id == previousCustomerId,
              );
              if (previousSelectedUser) {
                setSelectedUser([previousSelectedUser]);
              } else {
                // Fallback: fetch by id directly
                getCustomerByIdApi(previousCustomerId)
                  .then((custRes) => {
                    if (custRes?.status === "success" && custRes?.data) {
                      const customerObj =
                        custRes.data.customer || custRes.data;
                      if (customerObj && customerObj.id) {
                        setSelectedUser([customerObj]);
                      }
                    }
                  })
                  .catch(() => { });
              }
            }
          }
        }
      })
      .catch((err) => console.log(err));

    if (type !== "update") {
      form.reset();
      dispatch(clearItemVariants());
      return;
    }

    if (updateItem) {
      const {
        type: giftType,
        reward,
        gift_card_items,
        note,
        template_id,
        expiry_date,
        status,
        usage_limit,
        is_multiple_usage,
        terms_and_conditions,
        is_conjunction,
        template_note,
        order_types,
        payment_methods,
      } = updateItem;

      form.reset({
        actionType: "update",
        type: giftType || "fixed",
        reward: reward || "",
        gift_card_items: gift_card_items || [],
        note: note || "",
        template_id: template_id || "",
        expiry_date: expiry_date ? expiry_date.split("T")[0] : "",
        status: status || "active",
        usage_limit: usage_limit || "",
        is_multiple_usage: Boolean(is_multiple_usage),
        terms_and_conditions: Boolean(terms_and_conditions),
        is_conjunction: Boolean(is_conjunction),
        template_note: template_note || "",
        template_note_enabled: Boolean(template_note),
        order_types: order_types || [],
        payment_methods: payment_methods || [],
        order_type_status: order_types && order_types.length > 0,
        payment_method_status: payment_methods && payment_methods.length > 0,
      });

      dispatch(setItemVariants(gift_card_items || []));
    }
  }, [dispatch, type, updateItem]);

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(
        fetchLoyaltyTemplates({
          restaurant_id: user.restaurant_id,
          type: "gift_card",
          per_page: 10,
        }),
      );
    }
  }, [dispatch, user?.restaurant_id]);

  const orderedTemplates = sortTemplatesBySerial(templates || []);
  const selectedTemplateId = form.watch("template_id");
  const selectedTemplate = getTemplateForRecord(
    orderedTemplates,
    selectedTemplateId,
  );
  const SelectedTemplateComponent = getComponentByTemplate(
    selectedTemplate,
    GIFT_TEMPLATE_COMPONENTS,
  );

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;

    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }

    form.setValue("gift_card_items", allItem);
  };

  const handleRemove = async (id) => {
    await dispatch(removeItemVariant(id));
    await handleItemUpdate(id, "remove");
    toast.success("Item removed successfully");
  };

  const addGiftCardData = async (data) => {
    if (!data.is_multiple_usage && selectedUser.length < 1) {
      toast.error("Select customer is required");
      return;
    }

    setLoading(true);

    const payload = {
      ...data,
      reward: data.type === "food_item" ? null : data.reward,
      restaurant_id: user.restaurant_id,
      customer_id: data.is_multiple_usage ? null : selectedUser[0]?.id,
      usage_limit: data.is_multiple_usage ? data.usage_limit : "",
      gift_card_items: isFoodItem ? giftCardItems : [],
      template_note: data.template_note_enabled ? data.template_note : "",
    };

    try {
      let res;

      if (type === "update" && updateItem?.id) {
        res = await editGiftCardApi(
          {
            ...payload,
            gift_card_history_id: updateItem.gift_card_histories?.[0]?.id,
          },
          updateItem.id,
        );
      } else {
        res = await addGiftCardApi(payload);
      }

      if (res.status === "success") {
        toast.success(
          type === "update"
            ? "Gift Card updated successfully!"
            : "Gift Card added successfully!",
        );

        navigate("/loyalty/gift-card");
        reset();
        dispatch(clearItemVariants());
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Gift Card");
    } finally {
      setLoading(false);
    }
  };

  if (menuLoading) return <LoyaltyCreateSkeleton links={loyaltyLinks} />;

  return (
    <Layout2
      pageName={{ title: "Gift Card" }}
      paths={[
        { name: "Loyalty", path: "/loyalty" },
        { name: "Gift Card", path: "/loyalty/gift-card" },
        { name: type === "update" ? "Update Gift Card" : "Add New Gift Card" },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={loyaltyLinks} />}
    >
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite">
          <CardHeader>
            <CardTitle className="text-cwhite">
              {form.watch("actionType") === "update"
                ? "Update Gift Card"
                : "Add New Gift Card"}
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-8">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(addGiftCardData)}
                  className="flex flex-col gap-8"
                >
                  <FormInputField
                    name="type"
                    style="bg-cblack3"
                    placeholder="Select reward type"
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      // { label: "Redeem free item", value: "food_item" },
                      { label: "Redeem Cash Discount", value: "fixed" },
                      { label: "Redeem % Discount", value: "percentage" },
                    ]}
                    label="Reward Type"
                    required={true}
                  />

                  {isFoodItem && (
                    <div className="border border-white/10 p-5 rounded-md bg-cblack3">
                      <div className="flex items-center justify-between gap-5">
                        <h1 className="font-bold text-cwhite">Add Reward</h1>
                        <Button
                          type="button"
                          className="bg-cyellow text-black"
                          onClick={() => setOpenDialog(true)}
                        >
                          Add Reward Item
                        </Button>
                      </div>

                      {itemVariants && itemVariants.length > 0 && (
                        <Table className="mt-4">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item Name</TableHead>
                              <TableHead>Image</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {itemVariants.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {item?.item_name ||
                                    item?.menu_item?.name ||
                                    "Data not available"}
                                </TableCell>
                                <TableCell>
                                  {item.file_manager_url ? (
                                    <img
                                      src={item.file_manager_url}
                                      alt={item.item_name}
                                      className="w-12 h-12 rounded"
                                    />
                                  ) : (
                                    "No Image"
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.price !== undefined
                                    ? `£ ${item.price}`
                                    : "N/A"}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className="flex items-center justify-end gap-3">
                                    <Trash2
                                      onClick={() => handleRemove(item?.unique_id)}
                                      className="w-4 h-4 text-cred cursor-pointer"
                                    />
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}

                  {(isFixed || isPercentage) && (
                    <FormInputField
                      name="reward"
                      style="bg-cblack3"
                      placeholder="Enter reward amount"
                      form={form}
                      type="number"
                      label="Reward"
                      required={true}
                    />
                  )}

                  <FormInputField
                    name="note"
                    style="bg-cblack3"
                    placeholder="Enter title"
                    form={form}
                    type="text"
                    label="Title"
                    required={true}
                  />

                  <FormInputField
                    name="expiry_date"
                    placeholder="dd/mm/yy"
                    form={form}
                    type="date"
                    dateFormat="yyyy-MM-dd"
                    style="bg-cblack3"
                    label="Expiry Date"
                  />

                  <FormInputField
                    name="status"
                    style="bg-cblack3"
                    placeholder="Select Status"
                    form={form}
                    type="select"
                    parseAs="string"
                    selectOption={[
                      { label: "Active", value: "active" },
                      { label: "Inactive", value: "inactive" },
                    ]}
                    label="Status"
                    required={true}
                  />

                  <FormInputField
                    name="is_multiple_usage"
                    form={form}
                    type="checkbox"
                    style="bg-cblack3"
                    label="Multiple Customer"
                  />

                  {isMultipleUsage ? (
                    <FormInputField
                      name="usage_limit"
                      placeholder="Number of Customer"
                      form={form}
                      type="number"
                      style="bg-cblack3"
                      required={true}
                      label="Number of customer"
                    />
                  ) : (
                    <CustomerMultiSelect
                      options={customer}
                      value={selectedUser}
                      onChange={setSelectedUser}
                    />
                  )}

                  <div>
                    <div className="flex items-center">
                      <Switch
                        checked={form.watch("terms_and_conditions")}
                        onCheckedChange={(val) => {
                          form.setValue("terms_and_conditions", val);
                        }}
                        className="data-[state=unchecked]:bg-neutral-500"
                      />
                      <Label className="ml-2">Terms & Conditions</Label>
                    </div>

                    {form.watch("terms_and_conditions") && (
                      <Card className="bg-cblack text-cwhite mt-2">
                        <CardContent className="pt-5">
                          <div className="flex flex-col gap-7">
                            <section className="flex flex-col gap-3 bg-cblack5 p-3 rounded-md">
                              <div className="mb-1 flex items-center">
                                <Switch
                                  checked={form.watch("order_type_status")}
                                  onCheckedChange={(val) => {
                                    form.setValue("order_type_status", val);
                                    if (!val) {
                                      form.setValue("order_types", []);
                                    }
                                  }}
                                  className="data-[state=unchecked]:bg-neutral-500"
                                />
                                <Label className="ml-2">Order Type</Label>
                              </div>

                              {form.watch("order_type_status") && (
                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                  {ORDER_TYPE_OPTIONS.map((option) => {
                                    const isChecked = (
                                      form.watch("order_types") || []
                                    ).includes(option.value);

                                    return (
                                      <label
                                        key={option.value}
                                        className="flex cursor-pointer items-center gap-3 rounded-md border border-white/10 bg-cblack6 px-3 py-3 text-sm text-cwhite transition-colors hover:border-cyellow/60"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={(checked) => {
                                            const current =
                                              form.watch("order_types") || [];
                                            form.setValue(
                                              "order_types",
                                              toggleArrayValue(
                                                current,
                                                option.value,
                                                Boolean(checked),
                                              ),
                                            );
                                          }}
                                          className="border-cwhite2 data-[state=checked]:border-cyellow data-[state=checked]:bg-cyellow data-[state=checked]:text-black"
                                        />
                                        <span>{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </section>

                            <section className="flex flex-col gap-3 bg-cblack5 p-3 rounded-md">
                              <div className="mb-1 flex items-center">
                                <Switch
                                  checked={form.watch("payment_method_status")}
                                  onCheckedChange={(val) => {
                                    form.setValue("payment_method_status", val);
                                    if (!val) {
                                      form.setValue("payment_methods", []);
                                    }
                                  }}
                                  className="data-[state=unchecked]:bg-neutral-500"
                                />
                                <Label className="ml-2">Payment Method</Label>
                              </div>

                              {form.watch("payment_method_status") && (
                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                  {PAYMENT_METHOD_OPTIONS.map((option) => {
                                    const isChecked = (
                                      form.watch("payment_methods") || []
                                    ).includes(option.value);

                                    return (
                                      <label
                                        key={option.value}
                                        className="flex cursor-pointer items-center gap-3 rounded-md border border-white/10 bg-cblack6 px-3 py-3 text-sm text-cwhite transition-colors hover:border-cyellow/60"
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          onCheckedChange={(checked) => {
                                            const current =
                                              form.watch("payment_methods") || [];
                                            form.setValue(
                                              "payment_methods",
                                              toggleArrayValue(
                                                current,
                                                option.value,
                                                Boolean(checked),
                                              ),
                                            );
                                          }}
                                          className="border-cwhite2 data-[state=checked]:border-cyellow data-[state=checked]:bg-cyellow data-[state=checked]:text-black"
                                        />
                                        <span>{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </section>

                            <div className="mb-5 flex items-center p-3 bg-cblack5 rounded-md">
                              <Switch
                                checked={form.watch("is_conjunction")}
                                onCheckedChange={(val) => {
                                  form.setValue("is_conjunction", val);
                                }}
                                className="data-[state=unchecked]:bg-neutral-500"
                              />
                              <Label className="ml-2">
                                Not conjunction with any other offer
                              </Label>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="mt-4 rounded-md border border-white/10 bg-cblack5 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center">
                          <Switch
                            checked={form.watch("template_note_enabled")}
                            onCheckedChange={(val) => {
                              form.setValue("template_note_enabled", val);
                            }}
                            className="data-[state=unchecked]:bg-neutral-500"
                          />
                          <Label className="ml-2">Template Notes</Label>
                          <span className="ml-2 text-xs text-cyellow">
                            (Will show on card)
                          </span>
                        </div>
                        <span className="text-sm text-cwhite2">
                          Max 100 characters
                        </span>
                      </div>

                      {form.watch("template_note_enabled") && (
                        <div className="mt-4">
                          <FormInputField
                            name="template_note"
                            style="bg-cblack5"
                            placeholder="Write something"
                            form={form}
                            type="textarea"
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 mt-4">
                      <Label>Select Template</Label>

                      {templateLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-12 w-full rounded" />
                        </div>
                      ) : (
                        <FormInputField
                          name="template_id"
                          style="bg-cblack7 flex-1"
                          placeholder="Select Template"
                          form={form}
                          type="select"
                          search={true}
                          parseAs="number"
                          required={true}
                          selectOption={
                            orderedTemplates && orderedTemplates.length > 0
                              ? [
                                { label: "Select Template", value: "", disabled: true },
                                ...orderedTemplates.map((template) => ({
                                  label: template.title || "Untitled",
                                  value: template.id,
                                })),
                              ]
                              : [
                                { label: "No templates available", value: "", disabled: true },
                              ]
                          }
                        />
                      )}

                      {SelectedTemplateComponent && selectedTemplate ? (
                        <div className="w-[546px] gap-6">
                          <div className="lg:col-span-1">
                            <SelectedTemplateComponent template={selectedTemplate} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 flex gap-5 w-full">
                    <Link className="w-full" to="/loyalty/gift-card">
                      <Button className="w-full bg-cred text-white">Cancel</Button>
                    </Link>

                    <Button isLoading={loading} className="w-full bg-cyellow">
                      {form.watch("actionType") === "update" ? " Update" : " Confirm"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>

        <CreateItem
          title="Add Course Item"
          isOpen={openDialog}
          onClose={() => setOpenDialog(false)}
          onSuccess={(item) => handleItemUpdate(item, "add")}
          quantity={false}
        />
      </div>
    </Layout2>
  );
}

export default AddGiftCard;

const addNewGiftCardSchema = z
  .object({
    reward: z.coerce.number().optional(),
    type: z.string().min(1, "Reward type is required"),
    note: z.string().min(1, "Note is required"),
    status: z.string().min(1, "Status is required"),
    template_id: z.coerce.number().min(1, "Template is required"),
    expiry_date: z.preprocess(
      (val) => {
        if (!val) return undefined;
        const date = new Date(val);
        return date.toISOString().split("T")[0];
      },
      z.string({ required_error: "End date is required" }),
    ),
    is_multiple_usage: z.boolean(),
    usage_limit: z.coerce.number().optional(),
    terms_and_conditions: z.boolean().optional(),
    is_conjunction: z.boolean().optional(),
    template_note: z.string().optional(),
    template_note_enabled: z.boolean().optional(),
    order_types: z.array(z.string()).optional(),
    payment_methods: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type !== "food_item") {
      if (!data.reward || data.reward < 1) {
        ctx.addIssue({
          path: ["reward"],
          message: "Reward is required",
          code: z.ZodIssueCode.custom,
        });
      }
    }

    if (data.is_multiple_usage) {
      if (!data.usage_limit || data.usage_limit < 1) {
        ctx.addIssue({
          path: ["usage_limit"],
          message: "Number of Customer is required",
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });